import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  Wallet,
  ClipboardCheck,
  SearchX,
  Utensils,
  MousePointerClick,
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  Activity
} from 'lucide-react';
import { api, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, Segmented, SectionTitle, ErrorBanner, Avatar, Button, cx } from '../components/ui';
import { BuyersChart, Change, GrowthChart, PlatformBreakdown, Sparkline, TrendChart, formatChange, pctChange } from '../components/DashboardCharts';
import {
  formatCompactCurrency,
  formatNumber,
  formatPercent,
  formatRelative,
  daysSince
} from '../utils/format';

const PERIOD_OPTIONS = [
  { value: 'yesterday', label: 'Hôm qua' },
  { value: 'today', label: 'Hôm nay' },
  { value: '7d', label: '7 ngày' },
  { value: '30d', label: '30 ngày' },
  { value: 'month', label: 'Tháng này' }
];

const PREV_LABEL = {
  yesterday: 'hôm kia',
  today: 'hôm qua',
  '7d': 'kỳ trước',
  '30d': 'kỳ trước',
  month: 'kỳ trước'
};

// Ngưỡng cảnh báo cron không đồng bộ đơn hàng
const SYNC_WARN_HOURS = 6;

const formatDecimal = (v) => new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 }).format(v);

// ---------- Stat tile ----------

// So sánh kỳ hiện tại với kỳ trước. goodWhenUp=false cho chỉ số "càng thấp càng tốt" (tỷ lệ hủy...)
const Delta = ({ current, previous, goodWhenUp = true, label, ratio = false }) => {
  if (previous === null || previous === undefined) return null;
  if (ratio) {
    const diff = (current - previous) * 100;
    const flat = Math.abs(diff) < 0.05;
    const good = flat ? null : (diff > 0) === goodWhenUp;
    return (
      <p className="mt-1 truncate text-xs text-gray-500">
        <span className={cx('tabular font-medium', good === true && 'text-emerald-700', good === false && 'text-red-600')}>
          {diff > 0 ? '+' : ''}{diff.toFixed(1)} điểm
        </span>{' '}vs {label}
      </p>
    );
  }
  if (!previous && !current) return <p className="mt-1 truncate text-xs text-gray-400">Không đổi so với {label}</p>;
  if (!previous) return <p className="mt-1 truncate text-xs text-gray-500">Mới so với {label}</p>;
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
      <Change pct={(current - previous) / Math.abs(previous)} goodWhenUp={goodWhenUp} className="flex-shrink-0" />
      <span className="truncate">vs {label}</span>
    </p>
  );
};

const StatTile = ({ label, value, sub, delta, spark, className }) => (
  <Card className={cx('p-3.5 md:p-4', className)}>
    <p className="truncate text-xs font-medium text-gray-500">{label}</p>
    <p className="tabular mt-1 truncate text-xl font-semibold tracking-tight text-gray-900 md:text-2xl">{value}</p>
    {sub && <p className="mt-0.5 truncate text-xs text-gray-500">{sub}</p>}
    {delta}
    {spark && <Sparkline values={spark} className="mt-2" />}
  </Card>
);

// ---------- Hàng đợi việc cần làm ----------

const ActionCard = ({ to, icon: Icon, title, count, detail, urgent }) => {
  const active = count > 0;
  return (
    <Link
      to={to}
      className="group flex min-w-0 items-start gap-3 bg-white p-3.5 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900"
    >
      <span className={cx('flex-shrink-0 rounded-lg p-2', active ? (urgent ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700') : 'bg-gray-100 text-gray-400')}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <span className={cx('tabular text-xl font-semibold', active ? 'text-gray-900' : 'text-gray-400')}>{formatNumber(count)}</span>
          <ChevronRight className="h-3.5 w-3.5 self-center text-gray-300 group-hover:text-gray-500" />
        </span>
        <span className="block truncate text-sm font-medium text-gray-700">{title}</span>
        {detail && <span className={cx('mt-0.5 block truncate text-xs', urgent && active ? 'font-medium text-red-700' : 'text-gray-500')}>{detail}</span>}
      </span>
    </Link>
  );
};

// ---------- Phân bố trạng thái đơn (part-to-whole) ----------

const STATUS_STYLE = {
  COMPLETED: { label: 'Hoàn thành', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  PENDING: { label: 'Chờ xử lý', bar: 'bg-amber-400', dot: 'bg-amber-400' },
  CANCELLED: { label: 'Đã hủy', bar: 'bg-red-500', dot: 'bg-red-500' }
};
const OTHER_STYLE = { bar: 'bg-gray-300', dot: 'bg-gray-300' };

const StatusBreakdown = ({ rows }) => {
  const total = rows.reduce((s, r) => s + r.count, 0);
  if (!total) return <p className="py-6 text-center text-sm text-gray-500">Chưa có đơn trong kỳ</p>;
  return (
    <div>
      <div className="flex h-3 w-full gap-[2px] overflow-hidden rounded-full">
        {rows.map((r) => (
          <div
            key={r.status}
            className={cx('h-full first:rounded-l-full last:rounded-r-full', (STATUS_STYLE[r.status] || OTHER_STYLE).bar)}
            style={{ width: `${(r.count / total) * 100}%` }}
            title={`${(STATUS_STYLE[r.status] || {}).label || r.status}: ${r.count}`}
          />
        ))}
      </div>
      <ul className="mt-3 space-y-2">
        {rows.map((r) => {
          const s = STATUS_STYLE[r.status] || { ...OTHER_STYLE, label: r.status };
          return (
            <li key={r.status} className="flex items-center gap-2 text-sm">
              <span className={cx('h-2.5 w-2.5 flex-shrink-0 rounded-full', s.dot)} />
              <span className="flex-1 text-gray-700">{s.label}</span>
              <span className="tabular font-medium text-gray-900">{formatNumber(r.count)}</span>
              <span className="w-12 text-right text-xs text-gray-500">{formatPercent(r.count / total, 0)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ---------- Trang ----------

const DashboardPage = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('yesterday');

  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery(
    ['dashboard', period],
    async () => (await api.get('/admin/dashboard', { params: { period } })).data.data,
    { refetchInterval: 60000, keepPreviousData: true }
  );

  // Tăng trưởng không phụ thuộc kỳ và thay đổi chậm: tải riêng, không làm mới mỗi phút
  const { data: growth, error: growthError, refetch: refetchGrowth } = useQuery(
    'dashboard-growth',
    async () => (await api.get('/admin/dashboard/growth')).data.data,
    { staleTime: 10 * 60 * 1000 }
  );

  const cur = data?.current;
  const prev = data?.previous;
  const prevLabel = PREV_LABEL[period];
  const actions = data?.actions;
  const health = data?.health;
  const liab = data?.liabilities;

  const alerts = useMemo(() => {
    const list = [];
    if (health?.hoursSinceSync != null && health.hoursSinceSync > SYNC_WARN_HOURS) {
      list.push(`Chưa có đơn hàng nào được import/cập nhật trong ${Math.round(health.hoursSinceSync)} giờ (lần cuối ${formatRelative(health.lastSyncAt)}). Kiểm tra cron-job.`);
    }
    if (liab?.negativeUsers > 0) {
      list.push(`${liab.negativeUsers} người dùng có số dư âm. Nên tính lại số dư ở trang Người dùng.`);
    }
    return list;
  }, [health, liab]);

  const spark = (key) => data?.trend?.map((d) => d[key]);
  const perBuyer = (m) => (m?.buyers ? m.profit / m.buyers : 0);

  const delta = (key, opts = {}) => <Delta current={cur?.[key] || 0} previous={prev?.[key] ?? 0} label={prevLabel} {...opts} />;

  if (isLoading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 animate-pulse rounded-lg bg-gray-200/60" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-200/60" />)}
        </div>
      </div>
    );
  }

  const oldestWithdrawalDays = daysSince(actions?.pendingWithdrawals?.oldest);
  const oldestLostDays = daysSince(actions?.pendingLostOrders?.oldest);

  return (
    <div className="space-y-6">
      {/* Header + chọn kỳ */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm text-gray-500">Xin chào, {user?.name || 'Admin'}</p>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 md:text-2xl">Tổng quan hoạt động</h1>
          </div>
          <Button variant="outline" size="icon" onClick={() => refetch()} aria-label="Làm mới" className="flex-shrink-0 md:hidden">
            <RefreshCw className={cx('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <Segmented options={PERIOD_OPTIONS} value={period} onChange={setPeriod} />
          <Button variant="outline" size="icon" onClick={() => refetch()} aria-label="Làm mới" className="hidden flex-shrink-0 md:inline-flex">
            <RefreshCw className={cx('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <ErrorBanner message={error ? (errorMessage(error, 'Không tải được dashboard')) : ''} onRetry={refetch} />

      {alerts.map((msg) => (
        <div key={msg} className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{msg}</span>
        </div>
      ))}

      {/* 1. Việc cần xử lý */}
      {actions && (
        <section>
          <SectionTitle>Cần xử lý</SectionTitle>
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            <ActionCard
              to="/withdrawals"
              icon={Wallet}
              title="Yêu cầu rút tiền"
              count={actions.pendingWithdrawals.count}
              detail={actions.pendingWithdrawals.count
                ? `${formatCompactCurrency(actions.pendingWithdrawals.amount)} · cũ nhất ${oldestWithdrawalDays ?? 0} ngày`
                : 'Không có yêu cầu chờ'}
              urgent={oldestWithdrawalDays >= 3}
            />
            <ActionCard
              to="/approval"
              icon={ClipboardCheck}
              title="Đơn chờ duyệt"
              count={actions.ordersReadyToApprove.count}
              detail={actions.ordersReadyToApprove.count
                ? `${formatCompactCurrency(actions.ordersReadyToApprove.amount)} hoàn tiền`
                : `Hoàn thành > ${actions.ordersReadyToApprove.minDays} ngày`}
            />
            <ActionCard
              to="/lost-orders"
              icon={SearchX}
              title="Đơn thất lạc"
              count={actions.pendingLostOrders.count}
              detail={actions.pendingLostOrders.count ? `Cũ nhất ${oldestLostDays ?? 0} ngày` : 'Không có yêu cầu'}
              urgent={oldestLostDays >= 7}
            />
            <ActionCard
              to="/shopeefood"
              icon={Utensils}
              title="ShopeeFood chờ"
              count={actions.pendingShopeeFood.count}
              detail="Đang xử lý / chờ duyệt tiền"
            />
            <ActionCard
              to="/clicks?platform=shopee"
              icon={MousePointerClick}
              title="Click rác > 48h"
              count={actions.staleUnverifiedClicks.count}
              detail="Chưa xác thực, có thể dọn"
            />
          </div>
        </section>
      )}

      {/* 2. Hiệu quả kinh doanh trong kỳ */}
      {cur && (
        <section>
          <SectionTitle>Hiệu quả kinh doanh</SectionTitle>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {/* Hero: lợi nhuận ước tính */}
            <div className="col-span-2 flex min-w-0 flex-col rounded-xl bg-gray-900 p-4 text-white md:row-span-2 md:p-5">
              <p className="text-sm text-gray-300">Lợi nhuận ước tính</p>
              <p className="tabular mt-1 text-4xl font-semibold tracking-tight text-gold-400 md:text-5xl">{formatCompactCurrency(cur.profit)}</p>
              <p className="mt-1.5 text-xs text-gray-300">
                {pctChange(cur.profit, prev?.profit) === null
                  ? `Kỳ trước (${prevLabel}): ${formatCompactCurrency(prev?.profit || 0)}`
                  : `${formatChange(pctChange(cur.profit, prev.profit))} so với ${prevLabel} (${formatCompactCurrency(prev.profit)})`}
              </p>
              <Sparkline values={spark('profit')} color="#FFC53D" className="mt-3 h-12 md:mt-auto" />
              <div className="mt-3 grid grid-cols-2 gap-3 border-t border-white/15 pt-3 text-sm">
                <div>
                  <p className="text-xs text-gray-400">Hoa hồng từ sàn</p>
                  <p className="tabular font-semibold">{formatCompactCurrency(cur.grossCommission)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400">Hoàn cho user</p>
                  <p className="tabular font-semibold">{formatCompactCurrency(cur.cashback)}</p>
                </div>
              </div>
            </div>

            <StatTile label="Đơn hợp lệ" value={formatNumber(cur.validOrders)} sub={`${formatNumber(cur.completedOrders)} đã hoàn thành`} delta={delta('validOrders')} spark={spark('validOrders')} />
            <StatTile label="Giá trị đơn (GMV)" value={formatCompactCurrency(cur.gmv)} sub={`TB ${formatCompactCurrency(cur.aov)}/đơn`} delta={delta('gmv')} spark={spark('gmv')} />
            <StatTile label="Người mua" value={formatNumber(cur.buyers)} sub={`${formatCompactCurrency(perBuyer(cur))} lợi nhuận/người`} delta={delta('buyers')} spark={spark('buyers')} />
            <StatTile label="Lượt click" value={formatNumber(cur.clicks)} sub={`${formatNumber(cur.clickers)} người click`} delta={delta('clicks')} spark={spark('clicks')} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="User mới" value={formatNumber(cur.newUsers)} delta={delta('newUsers')} spark={spark('newUsers')} />
            <StatTile
              label="Chuyển đổi click → đơn"
              value={formatPercent(cur.conversionRate)}
              delta={delta('conversionRate', { ratio: true })}
            />
            <StatTile
              label="Tỷ lệ hủy"
              value={formatPercent(cur.cancelRate)}
              sub={`${formatNumber(cur.cancelledOrders)} đơn hủy${cur.fraudItems ? ` · ${cur.fraudItems} SP gian lận` : ''}`}
              delta={delta('cancelRate', { ratio: true, goodWhenUp: false })}
            />
            <StatTile label="ShopeeFood" value={formatNumber(cur.shopeeFoodOrders)} sub={`${formatCompactCurrency(cur.shopeeFoodCommission)} đã duyệt`} delta={delta('shopeeFoodOrders')} />
          </div>
        </section>
      )}

      {/* 3. Xu hướng theo ngày + theo sàn */}
      {data?.trend?.length > 0 && (
        <section className="space-y-3">
          <SectionTitle className="mb-0">Xu hướng</SectionTitle>
          <TrendChart data={data.trend} previous={data.trendPrevious} />
          {data.platforms && <PlatformBreakdown platforms={data.platforms} previous={data.platformsPrevious} />}
        </section>
      )}

      {/* 4. Tăng trưởng dài hạn */}
      <section className="space-y-3">
        <SectionTitle className="mb-0">Tăng trưởng</SectionTitle>
        <ErrorBanner message={growthError ? errorMessage(growthError, 'Không tải được số liệu tăng trưởng') : ''} onRetry={refetchGrowth} />
        {growth ? (
          <>
            <GrowthChart growth={growth} />
            <BuyersChart months={growth.months} />
            <p className="text-xs text-gray-500">
              Lợi nhuận và GMV tính cả đơn chưa duyệt nên các tháng gần đây có thể giảm khi đơn bị hủy hoặc đối soát.
            </p>
          </>
        ) : !growthError && <div className="h-80 animate-pulse rounded-xl bg-gray-200/60" />}
      </section>

      {/* 5. Dòng tiền & công nợ */}
      {liab && cur && (
        <section>
          <SectionTitle>Dòng tiền & công nợ với user</SectionTitle>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="Số dư user có thể rút" value={formatCompactCurrency(liab.approved)} sub={`${formatNumber(liab.usersWithApproved)} user có số dư`} />
            <StatTile label="Số dư đang chờ duyệt" value={formatCompactCurrency(liab.pending)} sub="Từ đơn chưa đủ ngày" />
            <StatTile
              label="Đã chi trả trong kỳ"
              value={formatCompactCurrency(cur.withdrawalsPaidAmount)}
              sub={`${formatNumber(cur.withdrawalsPaid)} lệnh · ${formatNumber(cur.withdrawalsRequested)} yêu cầu mới`}
              delta={delta('withdrawalsPaidAmount')}
            />
            <StatTile
              label="Thời gian xử lý rút TB"
              value={cur.avgPayoutHours == null ? '—' : cur.avgPayoutHours >= 48 ? `${formatDecimal(cur.avgPayoutHours / 24)} ngày` : `${formatDecimal(cur.avgPayoutHours)} giờ`}
              sub={`Tổng đã chi: ${formatCompactCurrency(liab.totalPaidAllTime)}`}
              delta={cur.avgPayoutHours != null && prev?.avgPayoutHours != null
                ? <Delta current={cur.avgPayoutHours} previous={prev.avgPayoutHours} label={prevLabel} goodWhenUp={false} />
                : null}
            />
          </div>
        </section>
      )}

      {/* 6. Trạng thái đơn + top user */}
      {data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Trạng thái đơn trong kỳ</h3>
            <StatusBreakdown rows={data.statusBreakdown || []} />
          </Card>
          <Card className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Top người dùng theo tiền hoàn</h3>
            {data.topUsers?.length ? (
              <ul className="divide-y divide-gray-100">
                {data.topUsers.map((u, i) => (
                  <li key={u.id}>
                    <Link to={`/orders?user_id=${u.id}`} className="flex items-center gap-3 py-2.5">
                      <span className="tabular w-4 text-xs font-semibold text-gray-400">{i + 1}</span>
                      <Avatar name={u.name} email={u.email} className="h-8 w-8" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{u.name || u.email}</p>
                        <p className="truncate text-xs text-gray-500">{u.orders} đơn</p>
                      </div>
                      <span className="tabular text-sm font-semibold text-gray-900">{formatCompactCurrency(u.cashback)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-6 text-center text-sm text-gray-500">Chưa có dữ liệu trong kỳ</p>
            )}
          </Card>
        </div>
      )}

      {/* Sức khỏe hệ thống */}
      {health && (
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><Activity className="h-3.5 w-3.5" /> Đồng bộ đơn gần nhất: {formatRelative(health.lastSyncAt)}</span>
          <span>Tính số dư gần nhất: {formatRelative(liab?.lastBalanceCalculated)}</span>
          <span>Dashboard cập nhật: {formatRelative(dataUpdatedAt)}</span>
        </p>
      )}
    </div>
  );
};

export default DashboardPage;
